import { createReducer } from '@reduxjs/toolkit'

import { arrayToObject } from 'helpers/Functions'

import { SettingRow } from 'types/GlobalSimFlaw'
import {
  TSimFlawAggregatesDto,
  TSimFlawGroupDto
} from 'redux/api/internal/SimFlaws/contract'

import * as actions from 'redux/actions/internal/SimFlaws/actions'
import * as authenticationActions from 'redux/actions/Authentication/actions'

export interface SimFlawReducerType {
  /** The counts behind both KPI rows and the Översikt tables. */
  aggregates: TSimFlawAggregatesDto | null
  aggregatesFetching: boolean
  /**
   * Scope of the most recent aggregates request (null = all customers), kept
   * so a response for an older scope can be dropped rather than overwriting
   * the current one.
   */
  requestedAggregatesCustomerId: string | null
  /** One entry per affected table, most brister first. */
  groups: TSimFlawGroupDto[]
  /** Tables matching the filter, i.e. what the group pager pages over. */
  groupCount: number
  /** Rows in the one filtered table; null while several tables are shown. */
  rowCount: number | null
  groupsFetched: boolean
  groupsFetching: boolean
  /** Same idea as `requestedAggregatesCustomerId`, but for every filter. */
  requestedGroupsKey: string | null
  settings: { [key: string]: SettingRow }
  settingsFetched: boolean
}

// Initial state
const INITIAL_STATE: SimFlawReducerType = {
  aggregates: null,
  aggregatesFetching: false,
  requestedAggregatesCustomerId: null,
  groups: [],
  groupCount: 0,
  rowCount: null,
  groupsFetched: false,
  groupsFetching: false,
  requestedGroupsKey: null,
  settings: {},
  settingsFetched: false
}

// Reducer (createReducer uses immer — cases mutate the draft directly)
export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getGroups, (state, action) => {
      state.requestedGroupsKey = action.payload.requestKey
      state.groupsFetching = true
    })
    .addCase(actions.getGroupsSuccess, (state, action) => {
      const { payload } = action

      // Changing a filter fires a new request while the previous one is
      // still in flight, and the two can come back out of order. Writing a
      // superseded response would show rows the current filter excludes.
      if (payload.requestKey !== state.requestedGroupsKey) {
        return
      }

      state.groups = payload.data.groups
      state.groupCount = payload.data.group_count
      state.rowCount = payload.data.row_count
      state.groupsFetched = true
      state.groupsFetching = false
    })
    .addCase(actions.getGroupsFailed, (state, action) => {
      // Same guard: a superseded request failing must not clear the spinner
      // for the request that replaced it.
      if (action.payload.requestKey !== state.requestedGroupsKey) {
        return
      }

      state.groupsFetching = false
    })
    .addCase(actions.getAggregates, (state, action) => {
      state.requestedAggregatesCustomerId = action.payload.customerId ?? null
      state.aggregatesFetching = true
    })
    .addCase(actions.getAggregatesSuccess, (state, action) => {
      const { payload } = action

      if (
        (payload.customerId ?? null) !== state.requestedAggregatesCustomerId
      ) {
        return
      }

      state.aggregates = payload.data
      state.aggregatesFetching = false
    })
    .addCase(actions.getAggregatesFailed, (state, action) => {
      if (
        (action.payload.customerId ?? null) !==
        state.requestedAggregatesCustomerId
      ) {
        return
      }

      state.aggregatesFetching = false
    })
    .addCase(actions.getSettingsSuccess, (state, action) => {
      // arrayToObject is an untyped JS helper, so its `object` needs naming.
      state.settings = arrayToObject(action.payload.data, 'value') as {
        [key: string]: SettingRow
      }
      state.settingsFetched = true
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
})
